import { createContext, useContext, useState, useEffect } from 'react';
import { getUserProfile } from '../api/me';
import { getApplicantDetails } from '../api/getApplicantDetails';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => {
    const storedToken = localStorage.getItem('token');
    // Clear only applicant-related localStorage if no token found
    // Don't clear admin tokens
    if (!storedToken) {
      localStorage.removeItem('token');
      localStorage.removeItem('applicant');
      localStorage.removeItem('applicantData');
    }
    return storedToken;
  });
  const [user, setUser] = useState(null);
  const [applicantStatus, setApplicantStatus] = useState(() =>
    localStorage.getItem('applicant')
  );
  const [applicantData, setApplicantData] = useState(() => {
    const stored = localStorage.getItem('applicantData');
    return stored ? JSON.parse(stored) : null;
  });
  const isAuthenticated = !!token;

  const login = (newToken, userData) => {
    localStorage.setItem('token', newToken);
    setUser(userData);
    setToken(newToken);
  };

  const logout = () => {
    // Clear only applicant-related localStorage on logout
    // Don't clear admin tokens
    localStorage.removeItem('token');
    localStorage.removeItem('applicant');
    localStorage.removeItem('applicantData');
    setUser(null);
    setToken(null);
    setApplicantStatus(null);
    setApplicantData(null);
  };

  // Fetch user profile when token exists
  useEffect(() => {
    if (token && !user) {
      const res = getUserProfile();
      res
        .then((userData) => {
          setUser(userData);
        })
        .catch(() => logout());
    }
  }, [token]);

  // Fetch applicant details when user is available
  useEffect(() => {
    const fetchApplicantStatus = async () => {
      if (user?.id) {
        try {
          const applicant = await getApplicantDetails(user.id);
          if (applicant) {
            // Store full applicant object
            setApplicantData(applicant);
            localStorage.setItem('applicantData', JSON.stringify(applicant));

            // Also store status separately for backward compatibility
            if (applicant.applicationStatus) {
              setApplicantStatus(applicant.applicationStatus);
              localStorage.setItem('applicant', applicant.applicationStatus);
            }
          }
        } catch (error) {
          console.error('Error fetching applicant status:', error);
        }
      }
    };

    fetchApplicantStatus();
  }, [user?.id]);

  // Function to manually refresh applicant status
  const refreshApplicantStatus = async () => {
    if (user?.id) {
      try {
        const applicant = await getApplicantDetails(user.id);
        if (applicant) {
          // Store full applicant object
          setApplicantData(applicant);
          localStorage.setItem('applicantData', JSON.stringify(applicant));

          // Also store status separately for backward compatibility
          if (applicant.applicationStatus) {
            setApplicantStatus(applicant.applicationStatus);
            localStorage.setItem('applicant', applicant.applicationStatus);
          }
        }
      } catch (error) {
        console.error('Error refreshing applicant status:', error);
      }
    }
  };

  return (
    <AuthContext.Provider
      value={{
        token,
        user,
        setUser,
        applicantStatus,
        applicantData,
        refreshApplicantStatus,
        login,
        logout,
        isAuthenticated,
      }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
